import { createContext, useContext, useMemo, useCallback, useRef, useEffect } from "react";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { useAuthenticatedResource } from "../hooks/useAuthenticatedResource";
import { isCallableFunction, matchesFunction } from "../utils/functionCatalog";

const FunctionContext = createContext();
const EMPTY_FUNCTIONS = [];

function selectFunctions(data) {
    if (!Array.isArray(data) || data.some(item => typeof item?.Name !== "string")) {
        throw new Error("The function catalog returned an unexpected response.");
    }
    return data;
}

export function useFunctionContext() {
    const context = useContext(FunctionContext);
    if (!context) throw new Error("useFunctionContext must be used within a FunctionProvider");
    return context;
}

export function FunctionProvider({ children }) {
    const { instance, accounts } = useMsal();
    const accountId = accounts[0]?.homeAccountId;
    const { data, isLoading, error, reload } = useAuthenticatedResource("/api/functions", selectFunctions);
    const functionList = data || EMPTY_FUNCTIONS;
    const schemeCache = useRef(new Map());

    useEffect(() => {
        schemeCache.current = new Map();
    }, [instance, accountId]);

    const getFunctionByName = useCallback(name =>
        functionList.find(func => func.Name === name && isCallableFunction(func)), [functionList]);
    const getFilteredFunctions = useCallback((filter = "") =>
        functionList.filter(func => isCallableFunction(func) && matchesFunction(func, filter)), [functionList]);

    const getUIScheme = useCallback(functionName => {
        const cache = schemeCache.current;
        if (!cache.has(functionName)) {
            const request = (async () => {
                try {
                    const token = await instance.acquireTokenSilent({
                        scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/user_impersonation`],
                    });
                    const response = await axios.get(`${process.env.REACT_APP_ENDPOINT_URL}/api/UiScheme`, {
                        headers: { Authorization: `Bearer ${token.accessToken}` },
                        params: { FunctionName: functionName },
                    });
                    return response.data;
                } catch (error) {
                    const response = error.response;
                    if (response?.status === 404
                        || (response?.status === 400 && response.data === "Function UI scheme not found")) return null;
                    cache.delete(functionName);
                    throw error;
                }
            })();
            cache.set(functionName, request);
        }
        return cache.get(functionName);
    }, [instance]);

    const refreshFunctions = useCallback(() => {
        schemeCache.current = new Map();
        reload();
    }, [reload]);

    const value = useMemo(() => ({
        functionList, isLoading, error, isLoaded: data !== null,
        getFunctionByName, getFilteredFunctions, getUIScheme, refreshFunctions,
    }), [functionList, isLoading, error, data, getFunctionByName, getFilteredFunctions, getUIScheme, refreshFunctions]);

    return <FunctionContext.Provider value={value}>{children}</FunctionContext.Provider>;
}
